'use client'

import { useSession } from 'next-auth/react'
import { useState, useEffect, useMemo } from 'react'
import { TokenDetailsFragment } from '@/gql/generated/graphql'
import { getV2VaultsByWalletAddress } from '@/app/actions/user-vaults'

export interface TokenEditPermissionsResult {
  userAddress?: string
  authorizedAddresses: string[]
  isLoading: boolean
  error: Error | null
}

/**
 * Hook to determine if the current user has edit permissions for a token.
 * This includes both direct ownership and delegate.xyz v2 delegate permissions.
 *
 * @param token - The token to check permissions for
 * @returns Object containing user address and authorized addresses (user + delegate vaults)
 */
export function useTokenEditPermissions(_token: TokenDetailsFragment): TokenEditPermissionsResult {
  const { data: session } = useSession()
  const userAddress = session?.user?.publicAddress

  const [delegateAddresses, setDelegateAddresses] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  // Fetch delegate addresses when user address changes
  useEffect(() => {
    if (!userAddress) {
      setDelegateAddresses([])
      setIsLoading(false)
      setError(null)
      return
    }

    const fetchDelegates = async () => {
      setIsLoading(true)
      setError(null)

      try {
        const data = await getV2VaultsByWalletAddress(userAddress)
        const vaults = data.linkedVaults?.map((vault) => vault.vault.toLowerCase()) || []
        setDelegateAddresses(vaults)
      } catch (err) {
        console.error('Error fetching delegate addresses:', err)
        setError(err as Error)
        setDelegateAddresses([]) // Continue with just user address if delegate fetch fails
      } finally {
        setIsLoading(false)
      }
    }

    fetchDelegates()
  }, [userAddress])

  // Combine user address and delegate addresses
  const authorizedAddresses = useMemo(() => {
    const addresses: string[] = []

    if (userAddress) {
      addresses.push(userAddress.toLowerCase())
    }

    // Add delegate vault addresses
    addresses.push(...delegateAddresses)

    return addresses
  }, [userAddress, delegateAddresses])

  return {
    userAddress,
    authorizedAddresses,
    isLoading,
    error
  }
}
